import React from 'react';
import { MailOutline, PhoneInTalkOutlined, PlaceOutlined, SupervisedUserCircle } from '@mui/icons-material';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { useL10n } from '@uplift/hooks';
import { useCollaborativeProviderDetailsQuery, userContactsQuery, userDisplayNameQuery, useStatesQuery } from '@uplift/queries';
import { useSuspenseQueries } from '@tanstack/react-query';
import { ContactInfoRow } from '@uplift/ui-v2';


export type SuperviseeProviderProfileGeneralInfoProps = {
  providerId: string;
};


export const SuperviseeProviderProfileGeneralInfo: React.VFC<SuperviseeProviderProfileGeneralInfoProps> = ({ providerId }) => {
  const l10n = useL10n();
  const { data: collaborativeProviderDetails } = useCollaborativeProviderDetailsQuery(providerId);
  const [
    { data: displayName },
    { data: contacts }
  ] = useSuspenseQueries({
    queries: [

      userDisplayNameQuery(providerId),
      userContactsQuery(providerId)
    ]
  });
  const { data: states = [] } = useStatesQuery();
  const filteredContacts = contacts.filter(c => c.value);
  const licensedLabel = states
    .filter(state => collaborativeProviderDetails.licenseStateIds.includes(state.id))
    .map(state => state.shortName)
    .join(', ');

  const collaborativeStatesLabel = states
    .filter(state => collaborativeProviderDetails.collaborativeStateIds.includes(state.id))
    .map(state => state.shortName)
    .join(', ');

  return (
    <Box>
      <Box sx={{ p: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {displayName.full}
        </Typography>
        {displayName.suffix && (
          <Typography color="text.secondary">{displayName.suffix}</Typography>
        )}
      </Box>
      <Divider />
      <Stack spacing={2} sx={{ px: 3, py: 4 }}>
        {collaborativeProviderDetails.user.email && (
          <ContactInfoRow
            icon={MailOutline}
            primary={collaborativeProviderDetails.user.email}
            type="email"
          />
        )}
        {filteredContacts?.length ? filteredContacts.map(contact => (
          <ContactInfoRow
            key={contact.name}
            icon={PhoneInTalkOutlined}
            primary={contact.value}
            secondary={l10n.resources.contacts.labelByName[contact.name] ?? contact.name}
          />
        )) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PhoneInTalkOutlined fontSize="small" color="action" />
            <Typography>No phone number</Typography>
          </Box>
        )}
        {licensedLabel && (
          <ContactInfoRow
            icon={PlaceOutlined}
            primary={<>Licensed: {licensedLabel}</>}
          />
        )}
        {collaborativeStatesLabel && (
          <ContactInfoRow
            icon={SupervisedUserCircle}
            primary={<>Collaborative States: {collaborativeStatesLabel}</>}
          />
        )}
      </Stack>
    </Box>
  );
};
